/**
 * TierChip — Avika lead-quality pill + flags (Task A6, 2026-06-10).
 *
 * Renders the HOT / WARM / COLD tier as a coloured pill, plus optional
 * "B2B PRIORITY" (violet) and "OUT OF BUDGET" (slate) badges. Colours mirror
 * the Avika prompt-tester's Lead Quality panel
 * (/Users/mrb/AI/stellar/Avika/avika_prompt_tester_v3.jsx):
 *   B2B PRIORITY → #7c3aed (violet)   OUT OF BUDGET → #475569 (slate)
 * Tier semantics per the AURA spec: HOT teal/green, WARM amber, COLD grey.
 *
 * Tailwind classes only — no extra deps.
 */

type Tier = 'HOT' | 'WARM' | 'COLD' | null | undefined;

const TIER_CLASS: Record<'HOT' | 'WARM' | 'COLD', string> = {
  HOT: 'text-emerald-700 bg-emerald-50 border border-emerald-200',
  WARM: 'text-amber-700 bg-amber-50 border border-amber-200',
  COLD: 'text-gray-600 bg-gray-100 border border-gray-200',
};

export function TierChip({
  tier,
  isB2bPriority = false,
  isOutOfBudget = false,
}: {
  tier: Tier;
  isB2bPriority?: boolean;
  isOutOfBudget?: boolean;
}) {
  if (!tier) {
    return <span className="text-gray-400">—</span>;
  }
  const cls = TIER_CLASS[tier] ?? TIER_CLASS.COLD;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span className={`inline-flex items-center text-xs font-bold tracking-wide rounded px-2 py-0.5 ${cls}`}>
        {tier}
      </span>
      {isB2bPriority && (
        <span
          className="inline-flex items-center text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 text-white bg-violet-600"
          title="Prioritised B2B / channel lead"
        >
          B2B PRIORITY
        </span>
      )}
      {isOutOfBudget && (
        <span
          className="inline-flex items-center text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 text-white bg-slate-600"
          title="Customer indicated the price is out of budget"
        >
          OUT OF BUDGET
        </span>
      )}
    </span>
  );
}

export default TierChip;
